
import {defineComponent} from "vue";
import {FormMixin} from "@/components/base/FormMixin";
import BaseModalButton from "@/components/UI/Bootstrap/Modal/BaseModalButton.vue";
import BaseModal from "@/components/UI/Bootstrap/Modal/BaseModal.vue";
import {generateUUIDv4} from "@/helpers/functions/string";
import InputCheckbox from "@/components/UI/InputCheckbox.vue";
import InputRadio from "@/components/UI/InputRadio.vue";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import {ImageSelectionSettingsRestService} from "@/services/rest/photo-partner/ImageSelectionSettingsRestService";
import {
  ProductImageCreateRestDto,
  ProductImageOverviewRestDto,
  ProductImageSelectionOverviewResultsRestDto,
  ProductImageSelectionSettingsRestDto,
  ProductImageSettingsForLanguageRestDto
} from "@/api/pharma-photo-product-image/models";
import InputCheckboxMultiSelect from "@/components/UI/InputCheckboxMultiSelect.vue";
import {UIStateDto} from "@/dtos/UIStateDto";
import {PhotoRestService} from "@/services/rest/photo/PhotoRestService";
import InputImage from "@/components/UI/InputImage.vue";
import BaseDeleteButton from "@/components/UI/Bootstrap/BaseDeleteButton.vue";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {AutoSaveFeedbackStatusEnum} from "@/models/enum/AutoSaveFeedbackStatusEnum";
import {AbilityContext} from "@/context/AbilityContext";
import {I18nContext} from "@/context/I18nContext";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";

const PLACE_HOLDER = 'PLACE_HOLDER';

export default defineComponent({
  name: "ProductDetailPhotosTab",
  mixins: [FormMixin],
  components: {
    BaseOffcanvasButtonClose,
    BaseDeleteButton,
    InputImage,
    InputCheckboxMultiSelect,
    GenericFormWrapper, InputRadio, InputCheckbox, BaseModal, BaseModalButton
  },
  emits: ["savedSuccessfully"],
  props: {
    productCode: {
      type: String,
      required: true
    },
    cnkCode: {
      type: String,
      required: false
    },
    addCloseOffCanvasButton: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: () => ({
    modalId: generateUUIDv4('modal-upload-new-photo-') as string,
    checkedCheckbox: true,
    checkedRadioButton: 'checked',
    allLanguages: ['nl', 'fr'],
    productImages: null as ProductImageSelectionOverviewResultsRestDto | null,
    PLACE_HOLDER_VALUE: PLACE_HOLDER,
    placeHolderUrl: '',
    uploadImageUI: UIStateDto.createWithDefaults(),
    documentKey: null as string | null,
    temporaryProductImages: null as ProductImageSelectionOverviewResultsRestDto | null,
    hooveringOverImage: null as string | null,
    hoverImageX: 0,
    hoverImageY: 0
  }),
  mounted() {
    this.initialize();
  },
  computed: {
    DpModuleEnum() {
      return DpModuleEnum;
    },
    deductPixelsForFileColumn(): number {
      return (140 + (this.allLanguages.length * 50)) * 0.6;
    },
    deductPixelsForSourceColumn(): number {
      return (140 + (this.allLanguages.length * 50)) * 0.4;
    },
    showPlaceholderPhoto(): boolean {
      if (!this.productImages?.settings_per_language) {
        return false;
      }
      for (const language of this.allLanguages) {
        const perLanguageModel: ProductImageSettingsForLanguageRestDto | null = this.getLanguageSettings(language);
        if (perLanguageModel?.main_image_original_filename === PLACE_HOLDER) {
          return true;
        }
      }

      return false;
    },
    hoverImageStyle(): string {
      return `left: ${this.hoverImageX}px; top: ${this.hoverImageY}px;`;
    },
  },
  methods: {
    async doReload(): Promise<void> {
      this.productImages =
        await ImageSelectionSettingsRestService.getInstance().findProductImageSelectionSettings(this.productCode);
    },
    async doSubmit(): Promise<void> {
      await ImageSelectionSettingsRestService.getInstance()
        .syncProductImageSelectionSettings(this.productCode, this.productImages?.settings_per_language);
      this.$emit('savedSuccessfully');
    },
    async doDeleteItemWithKey(originalFilename: string): Promise<void> {
      for (const language of this.allLanguages) {
        const perLanguageModel: ProductImageSettingsForLanguageRestDto | null = this.getLanguageSettings(language);
        if (!perLanguageModel) {
          continue;
        }
        if (perLanguageModel.shown_images_original_filenames.includes(originalFilename)) {
          if (perLanguageModel.main_image_original_filename === originalFilename) {
            perLanguageModel.main_image_original_filename = PLACE_HOLDER;
          }
          perLanguageModel.shown_images_original_filenames =
            perLanguageModel.shown_images_original_filenames.filter((value: string) => value !== originalFilename);
        }
      }

      this.temporaryProductImages = this.productImages;

      await PhotoRestService.getInstance().deleteProductImage(this.productCode, originalFilename);
    },
    async afterDelete(): Promise<void> {
      await this.doReload();
      if (this.productImages) {
        this.productImages = {
          ...this.productImages,
          settings_per_language: this.temporaryProductImages?.settings_per_language
        };
        this.temporaryProductImages = null;
      }
    },
    async initialize(): Promise<void> {
      if (!AbilityContext.isAuthorizedForFeature('MULTILINGUAL')) {
        this.allLanguages = I18nContext.getLanguages();
      }
      this.placeHolderUrl = PhotoRestService.getInstance().getNotFoundUrl();
      await this.reloadContent();
      this.uploadImageUI
        .setCanSubmit(false)
        .setReady();
    },
    getLanguageSettings(language: string): ProductImageSettingsForLanguageRestDto | null {
      if (!this.productImages?.settings_per_language) {
        return null;
      }
      let settings: ProductImageSettingsForLanguageRestDto | null | undefined =
        this.productImages.settings_per_language[('settings_' + language.toLowerCase()) as keyof ProductImageSelectionSettingsRestDto];

      if (!settings) {
        settings = {
          main_image_original_filename: PLACE_HOLDER,
          shown_images_original_filenames: []
        };
        this.productImages.settings_per_language[('settings_' + language.toLowerCase()) as keyof ProductImageSelectionSettingsRestDto] = settings;
      }

      return settings;
    },
    selectPlaceholderForLanguage(language: string) {
      const perLanguageModel: ProductImageSettingsForLanguageRestDto = this.getLanguageSettings(language)!;
      perLanguageModel.main_image_original_filename = PLACE_HOLDER;
      // Empty the array of shown images, without replacing it, as this array is the v-model of the checkboxes
      while (perLanguageModel.shown_images_original_filenames.length) {
        perLanguageModel.shown_images_original_filenames.pop();
      }
      this.productImages = {...this.productImages};
      this.onFormDataChanged();
    },
    selectMainImageForLanguage(language: string, imageOriginalFilename: string) {
      const perLanguageModel: ProductImageSettingsForLanguageRestDto = this.getLanguageSettings(language)!;
      perLanguageModel.main_image_original_filename = imageOriginalFilename;
      if (!perLanguageModel.shown_images_original_filenames.includes(imageOriginalFilename)) {
        perLanguageModel.shown_images_original_filenames.push(imageOriginalFilename);
      }
      this.productImages = {...this.productImages};
      this.onFormDataChanged();
    },
    changeShowImageForLanguage(language: string, imageOriginalFilename: string) {
      const perLanguageModel: ProductImageSettingsForLanguageRestDto = this.getLanguageSettings(language)!;
      if (perLanguageModel.shown_images_original_filenames.includes(imageOriginalFilename)) {
        if (perLanguageModel.main_image_original_filename === PLACE_HOLDER) {
          perLanguageModel.main_image_original_filename = imageOriginalFilename;
        }
      } else {
        if (perLanguageModel.main_image_original_filename === imageOriginalFilename) {
          // We need to select another main image.  First check if there is another image shown
          if (perLanguageModel.shown_images_original_filenames.length) {
            perLanguageModel.main_image_original_filename = perLanguageModel.shown_images_original_filenames[0];
          } else {
            perLanguageModel.main_image_original_filename = PLACE_HOLDER;
          }
        }
      }
      this.productImages = {...this.productImages};
      this.onFormDataChanged();
    },
    async submitAddProductImage(): Promise<void> {
      await this.performGenericSubmit(this.doSubmitAddProductImage, this.afterAddProductImage, this.uploadImageUI as UIStateDto);
    },
    async doSubmitAddProductImage(): Promise<void> {
      if (!this.documentKey) {
        return;
      }

      this.temporaryProductImages = this.productImages;

      const createDto: ProductImageCreateRestDto = {document_key: this.documentKey};
      await PhotoRestService.getInstance().createProductImage(this.productCode, createDto);
    },
    async afterAddProductImage(): Promise<void> {
      this.closeModal(this.modalId);
      await this.doReload();
      if (this.productImages) {
        this.productImages.settings_per_language = this.temporaryProductImages?.settings_per_language;

        const oldOriginalFilenames: string[] =
          (this.temporaryProductImages?.images ?? []).map((image: ProductImageOverviewRestDto) => image.original_filename);
        const newOriginalFilenames: string[] =
          (this.productImages?.images ?? []).map((image: ProductImageOverviewRestDto) => image.original_filename);
        const newImageOriginalFilename: string | undefined =
          newOriginalFilenames.find(filename => !oldOriginalFilenames.includes(filename));

        for (const language of this.allLanguages) {
          const perLanguageModel: ProductImageSettingsForLanguageRestDto | null = this.getLanguageSettings(language);
          if (!perLanguageModel) {
            continue;
          }
          if (this.productImages.images?.length === 1) {
            // If the first product image is added, we select that image for all languages.
            perLanguageModel.shown_images_original_filenames = [this.productImages.images[0].original_filename];
            perLanguageModel.main_image_original_filename = this.productImages.images[0].original_filename;
          } else {
            // Else we show the newly added image in all languages (which is the default behavior at the server)
            if (newImageOriginalFilename) {
              perLanguageModel.shown_images_original_filenames.push(newImageOriginalFilename);
            }
          }
        }

        this.temporaryProductImages = null;
        this.productImages = {...this.productImages};
      }
      this.pageUI.setAutoSaveFeedbackStatus(AutoSaveFeedbackStatusEnum.UNSAVED_CHANGES);
    },
    imageCleared(): void {
      this.documentKey = null;
      this.uploadImageUI
        .setCanSubmit(false)
        .clearError();
    },
    async imageUploaded(docKey: string | null): Promise<void> {
      this.uploadImageUI
        .setCanSubmit(docKey !== null)
        .clearError();
    },
    onBeforeModalOpened(): void {
      this.documentKey = null;
      this.uploadImageUI.setCanSubmit(false);
    },
    setIsHovering(imageUrl: string | null, event: MouseEvent): void {
      this.hooveringOverImage = imageUrl;
      if (event && imageUrl) {
        const target = event.target as HTMLImageElement;
        const rect = target.getBoundingClientRect();
        this.hoverImageX = rect.left;
        this.hoverImageY = rect.bottom;
      }
    },
  },
});
